<script setup lang="ts">
import { onMounted } from 'vue';
import { can, useDateTime } from '@/composables';
import { useStudentReferralStore } from '@/store';
import { PERMISSIONS } from '@/enum';

const { referrals_caseload, loading, getReferralCaseload } = useStudentReferralStore();

onMounted(() => init());

const init = async () => {
  if (can([PERMISSIONS.REFERRAL_CASELOAD])) await getReferralCaseload();
};

const onSort = async (event) => {
  const filters = {
    order_by: event.sortField,
    order: event.sortOrder === 1 ? 'asc' : 'desc',
  };
  await getReferralCaseload(filters);
};
</script>

<template>
  <Card class="page-content-full">
    <template #header>
      <div class="align-items-center flex justify-content-between w-full">
        <div class="flex">
          <Icon name="group" class="material-icons mr-2 text-primary" />
          <h2>Caseload Additions</h2>
          <small class="flex line-height-4">These students have been added to your caseload in the past 14 days</small>
        </div>
        <div class="align-items-center bg-light-gray border-round-sm flex justify-content-center p-2">
          <p class="text-dark text-xs mr-1">Total</p>
          <div v-if="!loading" class="text-sm text-primary-dark font-bold" v-text="referrals_caseload.total" />
          <Skeleton v-else shape="rectangle" size="10px" />
        </div>
      </div>
    </template>
    <template #content>
      <table v-if="loading" cellspacing="0" role="table" class="w-full p-datatable pl-3">
        <thead class="p-datatable-thead" role="rowgroup">
          <tr role="row">
            <th class="p-sortable-column justify-content-center mx-0">
              <div class="p-column-header-content pl-1">
                <span class="p-column-title">Organization</span>
                <span class="p-sortable-column-icon pi pi-fw pi-sort-alt"></span>
              </div>
            </th>
            <th class="p-sortable-column justify-content-center">
              <div class="p-column-header-content pl-1">
                <span class="p-column-title">Student</span>
                <span class="p-sortable-column-icon pi pi-fw pi-sort-alt"></span>
              </div>
            </th>
            <th class="justify-content-center p-sortable-column">
              <div class="p-column-header-content pl-1">
                <span class="p-column-title">Referral Type</span>
              </div>
            </th>
            <th class="p-sortable-column justify-content-center">
              <div class="p-column-header-content pl-1">
                <span class="p-column-title">Date of Referral</span>
                <span class="p-sortable-column-icon pi pi-fw pi-sort-alt"></span>
              </div>
            </th>
          </tr>
        </thead>
        <tr v-for="i in new Array(4)" :key="i">
          <td class="w-3"><Skeleton class="my-1" height="40px" /></td>
          <td class="w-3"><Skeleton class="my-1" height="40px" /></td>
          <td class="w-3"><Skeleton class="my-1" height="40px" /></td>
          <td class="w-3"><Skeleton class="my-1" height="40px" /></td>
        </tr>
      </table>
      <div class="p-datatable p-component p-datatable-scrollable p-datatable-responsive-scroll mx-3">
        <div class="p-datatable-wrapper px-3">
          <DataTable
            :value="referrals_caseload.data"
            data-key="id"
            loading-icon="logo-spinner"
            :loading="loading"
            responsive-layout="scroll"
            scrollable
            scroll-height="350px"
            @sort="onSort"
          >
            <template #empty>No students assigned</template>
            <template v-if="!loading">
              <Column field="organization" header="Organization" sortable>
                <template #body="slotProps">
                  <RouterLink
                    :to="`/organizations/${slotProps.data.organization_id}`"
                    class="font-bold"
                    v-text="slotProps.data.organization_name"
                  />
                </template>
              </Column>
              <Column field="student" header="Student" sortable>
                <template #body="slotProps">
                  <RouterLink
                    :to="`/user/${slotProps.data.student_id}`"
                    class="font-bold"
                    v-text="slotProps.data.student_name"
                  />
                </template>
              </Column>
              <Column field="referral_type_title" header="Referral Type" />
              <Column field="referred_at" header="Date" sortable>
                <template #body="{ data }">
                  <span v-text="useDateTime().formatIntlDate(data.referred_at)" />
                </template>
              </Column>
            </template>
          </DataTable>
        </div>
        <div class="p-column-resizer-helper hidden" />
      </div>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  padding: 0px !important;

  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
  }

  .p-datatable-wrapper {
    margin: 0px !important;
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}

.bg-light-gray {
  background: $grayLight;
}

.text-primary-dark {
  color: var(--primary-color-dark);
}

.text-dark {
  color: #3b4242;
}

.text-red-600 {
  color: $red;
}

.bg-extra-light {
  background: var(--primary-color-extra-light);
}

.text-dark-red {
  color: #822508;
}

.text-dark-medium {
  color: var(--text-color-secondary);
}

.text-primary-medium {
  color: #8bcfcf;
}
</style>
